// W.6: czyste funkcje (bez React/axios/fetch) dla checklisty "Pierwsze kroki" na dashboardzie.
// Postęp liczony ZE STANU DANYCH (liczniki + kompletność danych firmy), nigdy z flagi w bazie —
// stąd brak migracji. Kolejność kroków odzwierciedla realne zależności: dane firmy psują
// podgląd umowy/oferty dopóki są puste (patrz utils/companyData.js), więc idą pierwsze.
import { getMissingCompanyFields } from './companyData';

const DISMISS_KEY = 'onboardingChecklistDismissed';

// Zamknięcie ma być per-użytkownik (wzorzec demoTourSeen:v1:<userId> / pageTourSeen:v1:<userId>:…),
// inaczej dismiss na jednym koncie w tej przeglądarce chowa checklistę KAŻDEMU kolejnemu
// koncu logującemu się na tym samym komputerze — także zupełnie nowej organizacji, która
// nigdy jej nie widziała. `userId` brakuje tylko w testach jednostkowych bez zalogowanej
// sesji; wtedy zostajemy na kluczu bez sufiksu (zachowanie sprzed tej zmiany).
const dismissKey = (userId) => (userId ? `${DISMISS_KEY}:v1:${userId}` : DISMISS_KEY);

// Licznik może być undefined/null, gdy jego fetch w home.js padnie (Promise.allSettled) —
// traktujemy to jak "nie wiem", NIGDY jak "zrobione", więc krok zostaje widoczny.
// UWAGA NA TYP: `/api/kpis` oddaje liczniki jako NAPISY, bo `COUNT()` w Postgresie wraca
// przez `pg` jako string (`totalCustomers: "96"`). Twarde `typeof === 'number'` odrzucało
// więc prawdziwych klientów i punkt „Dodaj pierwszego klienta" zostawał nieodhaczony NA
// ZAWSZE, przez co lista „Pierwsze kroki" nigdy się nie domykała i nigdy nie znikała.
// Zmierzone na działającym panelu: kafelek „96 KLIENCI" obok listy twierdzącej, że klienta
// nie ma. Testy tego nie złapały, bo wszystkie podawały liczby, czyli kształt wyobrażony
// przez autora, a nie ten, który zwraca API.
// Napisy przeliczamy, ale `undefined`/`null` nadal znaczą „nie wiem", nigdy „zrobione".
const isPositiveCount = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const liczba = typeof value === 'string' ? Number(value) : value;
  return typeof liczba === 'number' && Number.isFinite(liczba) && liczba > 0;
};

export function buildOnboardingSteps({ equipmentCount, clientsCount, reservationsCount, contractSettings, inventoryConfigured } = {}) {
  return [
    {
      key: 'companyData',
      // getMissingCompanyFields traktuje null/undefined jak pusty obiekt (wszystko brakuje),
      // więc nieudany fetch i faktycznie puste ustawienia dają ten sam, bezpieczny wynik: not done.
      done: getMissingCompanyFields(contractSettings).length === 0,
      href: '/contract-settings',
    },
    {
      key: 'inventoryFields',
      // E.4: `inventoryConfigured` to pole `configured` z GET /api/inventory-settings (E.2) —
      // undefined/null (fetch padł albo caller go nie przekazał) traktujemy jak "nie wiem",
      // NIGDY jak "zrobione", zgodnie z konwencją isPositiveCount powyżej.
      done: inventoryConfigured === true,
      // Trasa realna to /inventory/add-category (App.js) — InventorySettings.js nie ma
      // własnej ścieżki "/inventory-settings", mimo intuicyjnej nazwy komponentu.
      href: '/inventory/add-category',
    },
    {
      key: 'equipment',
      done: isPositiveCount(equipmentCount),
      href: '/inventory/add',
      secondaryHref: '/import?entity=equipment',
    },
    {
      key: 'client',
      done: isPositiveCount(clientsCount),
      href: '/clients',
      secondaryHref: '/import?entity=clients',
    },
    {
      key: 'reservation',
      done: isPositiveCount(reservationsCount),
      href: '/reservation',
    },
  ];
}

// Licznik dla kroku „Utwórz pierwszą rezerwację”. Krok pyta, czy firma umie już dodać rezerwację,
// więc liczą się WSZYSTKIE, także anulowane: `reservationsEver` z /api/kpis. `totalRentals` od
// przeglądu 28.09.2026 pomija anulowane (kafelek), więc firma z samymi anulowanymi (np. nieopłacona
// rezerwacja z widgetu anulowana przez serwer) widziała krok z powrotem nieodhaczony, choć Kalendarz
// pokazywał jej rezerwację. Serwer sprzed tego pola: `totalRentals`, jak dotąd.
// Gdy KPI padły, zapasem jest lista z okna strony głównej przed odsianiem anulowanych; obejmuje tylko
// najbliższe dni, więc pusta znaczy „nie wiem” (undefined), nie „zero”.
export function checklistReservationsCount({ kpisLoaded, kpis, reservations } = {}) {
  if (kpisLoaded) {
    const ever = kpis?.reservationsEver;
    if (ever !== undefined && ever !== null) return ever;
    return kpis?.totalRentals ?? 0;
  }
  return Array.isArray(reservations) && reservations.length > 0 ? reservations.length : undefined;
}

export function checklistProgress(steps) {
  const list = steps || [];
  const total = list.length;
  const done = list.filter((s) => s.done).length;
  return { done, total, complete: total > 0 && done === total };
}

export function isChecklistDismissed(userId) {
  try {
    return localStorage.getItem(dismissKey(userId)) === '1';
  } catch {
    return false;
  }
}

export function dismissChecklist(userId) {
  try {
    localStorage.setItem(dismissKey(userId), '1');
  } catch {
    // localStorage niedostępny (np. tryb prywatny) — ukrycie nie przetrwa reloadu, ale nie wywala UI
  }
}
